<template>
  <div class="recommendations-overlay" role="region" :aria-label="title">
    <h2 class="recommendations-overlay__title">{{ title }}</h2>

    <div class="recommendations-overlay__grid" role="list">
      <button
        v-for="item in items"
        :key="item.asset_uid"
        class="recommendations-overlay__card"
        role="listitem"
        :aria-label="`${item.name}${item.duration ? ` — ${formatDuration(item.duration)}` : ''}`"
        @click="handleSelect(item.asset_uid)"
      >
        <div class="recommendations-overlay__thumbnail">
          <!-- A 16:9 card, up to a third of a player wide, cropped by `object-fit: cover`. -->
          <img
            v-if="item.thumbnail_url"
            :src="withThumbnailSize(item.thumbnail_url, 640, 360, 'cover')"
            :alt="item.name"
            loading="lazy"
          />
          <div v-else class="recommendations-overlay__thumbnail-placeholder" />
          <span v-if="item.duration" class="recommendations-overlay__duration">
            {{ formatDuration(item.duration) }}
          </span>
          <div class="recommendations-overlay__play-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
        <span class="recommendations-overlay__name">{{ item.name }}</span>
      </button>
    </div>

    <button
      class="recommendations-overlay__replay"
      :aria-label="t('player.recommendations.replay')"
      @click="handleReplay"
    >
      <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" aria-hidden="true">
        <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" />
      </svg>
      {{ t('player.recommendations.replay') }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue'
import { useVideoI18n } from '../../../i18n'
import { withThumbnailSize } from '../../../../common/thumbnail'

interface RecommendationItem {
  asset_uid: string
  name: string
  thumbnail_url?: string
  duration?: number
}

const props = defineProps({
  payload: {
    type: Object as PropType<Record<string, unknown>>,
    default: () => ({}),
  },
})

const emit = defineEmits<{
  submit: [data: { action: string; assetUid?: string }]
}>()

const { t } = useVideoI18n()

const title = computed(() => (props.payload.title as string) || t('player.recommendations.title'))

const items = computed<RecommendationItem[]>(() => {
  const raw = props.payload.items
  if (!Array.isArray(raw)) return []
  return raw as RecommendationItem[]
})

function handleSelect(assetUid: string): void {
  emit('submit', { action: 'play', assetUid })
}

function handleReplay(): void {
  emit('submit', { action: 'replay' })
}

function formatDuration(seconds: number): string {
  if (!seconds) return ''
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  }
  return `${m}:${s.toString().padStart(2, '0')}`
}
</script>

<style scoped lang="scss">
.recommendations-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
  max-width: 90%;
  max-height: 90%;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0 16px;
  box-sizing: border-box;

  &__title {
    margin: 0;
    font-size: clamp(14px, 2.5vw, 18px);
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
    letter-spacing: 0.01em;
    flex-shrink: 0;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(140px, 100%), 1fr));
    gap: clamp(8px, 1.5vw, 14px);
    width: 100%;
  }

  &__card {
    display: flex;
    flex-direction: column;
    gap: 6px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 10px;
    padding: 0;
    cursor: pointer;
    overflow: hidden;
    transition: transform 200ms ease, border-color 200ms ease, box-shadow 200ms ease;
    text-align: left;
    color: inherit;
    font: inherit;

    &:hover {
      transform: translateY(-2px);
      border-color: rgba(255, 255, 255, 0.2);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);

      .recommendations-overlay__play-icon {
        opacity: 1;
        transform: translate(-50%, -50%) scale(1);
      }

      .recommendations-overlay__thumbnail img {
        transform: scale(1.05);
      }
    }

    &:active {
      transform: translateY(0);
    }

    &:focus-visible {
      outline: 2px solid #fff;
      outline-offset: 2px;
    }
  }

  &__thumbnail {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    background: rgba(0, 0, 0, 0.3);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 300ms ease;
    }
  }

  &__thumbnail-placeholder {
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02));
  }

  &__duration {
    position: absolute;
    bottom: 6px;
    right: 6px;
    padding: 2px 6px;
    background: rgba(0, 0, 0, 0.75);
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    color: #fff;
    line-height: 1.3;
    pointer-events: none;
  }

  &__play-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(0.8);
    opacity: 0;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    transition: opacity 200ms ease, transform 200ms ease;
    pointer-events: none;

    svg {
      margin-left: 2px;
    }
  }

  &__name {
    padding: 4px 10px 10px;
    font-size: clamp(12px, 1.5vw, 13px);
    font-weight: 500;
    color: rgba(255, 255, 255, 0.85);
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__replay {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 20px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 10px;
    color: rgba(255, 255, 255, 0.8);
    font-size: clamp(12px, 1.5vw, 13px);
    font-weight: 500;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 200ms ease, color 200ms ease;

    &:hover {
      background: rgba(255, 255, 255, 0.14);
      color: #fff;
    }

    &:focus-visible {
      outline: 2px solid #fff;
      outline-offset: 2px;
    }
  }
}

// Light theme
[data-vp-theme='light'] .recommendations-overlay {
  &__title {
    color: rgba(0, 0, 0, 0.9);
  }

  &__card {
    background: rgba(0, 0, 0, 0.04);
    border-color: rgba(0, 0, 0, 0.08);

    &:hover {
      border-color: rgba(0, 0, 0, 0.2);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    }

    &:focus-visible {
      outline-color: #000;
    }
  }

  &__name {
    color: rgba(0, 0, 0, 0.85);
  }

  &__replay {
    background: rgba(0, 0, 0, 0.05);
    border-color: rgba(0, 0, 0, 0.1);
    color: rgba(0, 0, 0, 0.7);

    &:hover {
      background: rgba(0, 0, 0, 0.08);
      color: #000;
    }

    &:focus-visible {
      outline-color: #000;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .recommendations-overlay {
    &__card,
    &__replay,
    &__play-icon {
      transition-duration: 1ms;
    }

    &__thumbnail img {
      transition-duration: 1ms;
    }
  }
}

</style>
